import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useState, useRef, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGenerateId, createInputHandler, Chart, calculateRepayment } from '@lendi-jam/atoms';
import getColour from '@lendi-ui/commons/colours';
import Grid from '@lendi-ui/grid';
import Error from '@lendi-ui/icon/Error';
import Help from '@lendi-ui/icon/Help';
import { RadioGroup } from '@lendi-ui/radio';
import { Input } from '@lendi-ui/text-input';
import { RepaymentType } from '@lendi/core-constants';
import { AnchorButton, Button } from '@lendi/ui/Button';
import { Text } from '@lendi/ui/Typography';
import numeral from 'numeral';
import { useForm } from 'react-hook-form';
import { Tooltip } from 'recharts';
import * as yup from 'yup';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { bg, fg, color } from '@lendi-ui/color';
import { p, my, mb, py, pl, mr, mx, px, pr, mt } from '@lendi-ui/spacing';
import styled from 'styled-components';
import { jsx, jsxs } from 'react/jsx-runtime';
import { gte, between } from '@lendi-ui/breakpoint';
import Container from '@lendi-ui/container';
import { Select } from '@lendi-ui/dropdown';
import LendiTooltip from '@lendi-ui/tooltip';
import { Heading, Body } from '@lendi-ui/typography';
import { embedMarketingTrackingIntoURL } from '@lendi-jam/jam-link';

var _templateObject$2;
var TooltipWrapper = styled.div(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  box-sizing: border-box;\n  box-shadow: 1px 2px 6px -3px black;\n  width: 240px;\n  height: 85px;\n  border-radius: 5px;\n  ", "\n  ", "\n\n  ", " {\n    row-gap: 20px;\n\n    ", " {\n      text-align: right;\n    }\n  }\n\n  strong {\n    font-size: 12px;\n  }\n"])), bg('shade.0'), p('sm'), Grid, "".concat(Grid.Unit, ":nth-child(even)"));

var GraphTooltip = function GraphTooltip(_ref) {
  var _payload$, _payload$2;
  var payload = _ref.payload,
    totalYears = _ref.totalYears;
  if (!payload || !totalYears) return null;
  return /*#__PURE__*/jsx(TooltipWrapper, {
    children: /*#__PURE__*/jsxs(Grid, {
      children: [/*#__PURE__*/jsx(Grid.Unit, {
        size: 8 / 12,
        children: /*#__PURE__*/jsx("strong", {
          children: "Principal remaining"
        })
      }), /*#__PURE__*/jsxs(Grid.Unit, {
        size: 4 / 12,
        children: ["$", (_payload$ = payload[0]) === null || _payload$ === void 0 || (_payload$ = _payload$.payload) === null || _payload$ === void 0 ? void 0 : _payload$.remainingAmount.toLocaleString()]
      }), /*#__PURE__*/jsx(Grid.Unit, {
        size: 8 / 12,
        children: /*#__PURE__*/jsx("strong", {
          children: "Years remaining"
        })
      }), /*#__PURE__*/jsx(Grid.Unit, {
        size: 4 / 12,
        children: totalYears - ((_payload$2 = payload[0]) === null || _payload$2 === void 0 || (_payload$2 = _payload$2.payload) === null || _payload$2 === void 0 ? void 0 : _payload$2.years)
      })]
    })
  });
};

var _templateObject$1, _templateObject2$1, _templateObject3$1, _templateObject4$1, _templateObject5$1, _templateObject6$1, _templateObject7$1, _templateObject8$1, _templateObject9$1, _templateObject10$1, _templateObject11$1, _templateObject12$1, _templateObject13$1, _templateObject14$1, _templateObject15$1, _templateObject16$1, _templateObject17$1, _templateObject18$1, _templateObject19$1, _templateObject20$1;
var Wrapper$1 = styled(Container)(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  ", "\n"])), my('md'));
var Calculator = styled.form(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n  background-color: #f8f8f8;\n  border-radius: 5px;\n  ", "\n"])), p({
  lg: 'lg',
  xs: 'md'
}));
var Title = styled(Heading)(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n  ", "\n"])), mb('md'));
var StyledHeading$1 = styled(Heading)(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteral(["\n  display: block;\n\n  small {\n    font-size: 18px;\n  }\n"])));
var GraphHeading = styled(StyledHeading$1)(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteral(["\n  ", "\n"])), py('sm'));
var StyledUnit = styled(Grid.Unit)(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  ", "\n  row-gap: 6px;\n\n  &:last-child {\n    ", "\n  }\n\n  & > div {\n    width: 100%;\n  }\n"])), mb('md'), mb('nil'));
var StyledRadioGroup = styled(RadioGroup)(_templateObject7$1 || (_templateObject7$1 = _taggedTemplateLiteral(["\n  justify-content: space-between;\n\n  legend {\n    display: none;\n  }\n\n  label {\n    width: unset;\n    ", "\n\n    &:first-child {\n      ", "\n    }\n\n    &:before {\n      left: 0;\n    }\n\n    &:after {\n      left: 6px;\n    }\n  }\n"])), pl('xl'), mr('xxs'));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var StyledSelect = styled(Select)(_templateObject8$1 || (_templateObject8$1 = _taggedTemplateLiteral(["\n  div {\n    ", "\n  }\n"])), fg('shade.500'));
var StyledButtonWrapper = styled.div(_templateObject9$1 || (_templateObject9$1 = _taggedTemplateLiteral(["\n  ", ";\n\n  ", "\n"])), mx('auto'), gte('lg')(_templateObject10$1 || (_templateObject10$1 = _taggedTemplateLiteral(["\n    max-width: 36%;\n  "]))));
var InputAfter = styled(Body)(_templateObject11$1 || (_templateObject11$1 = _taggedTemplateLiteral(["\n  font-weight: 100;\n  ", "\n  ", "\n"])), fg('shade.500'), px('xxs'));
var InputBefore = styled(InputAfter)(_templateObject12$1 || (_templateObject12$1 = _taggedTemplateLiteral(["\n  ", "\n"])), pr('xxxs'));
var ResultsWrapper = styled.div(_templateObject13$1 || (_templateObject13$1 = _taggedTemplateLiteral(["\n  ", "\n  font-family: 'Open Sans', sans-serif;\n  font-size: 14px;\n  border: 1px solid #e3e3e3;\n  border-bottom-left-radius: 5px;\n  border-bottom-right-radius: 5px;\n  box-sizing: border-box;\n"])), p({
  lg: 'lg',
  xs: 'md'
}));
var Summary = styled(Body)(_templateObject14$1 || (_templateObject14$1 = _taggedTemplateLiteral(["\n  ", "\n"])), my('xxs'));
var PaymentSummary = styled.div(_templateObject15$1 || (_templateObject15$1 = _taggedTemplateLiteral(["\n  border-radius: 5px;\n  box-sizing: border-box;\n  color: #464646;\n  font-size: 12px;\n  width: 100%;\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n\n  ", "\n  ", ";\n\n  ", " {\n    row-gap: 15px;\n  }\n"])), bg('shade.25'), mb('xxxl'), mt('xxl'), mx('auto'), p('md'), gte('sm')(_templateObject16$1 || (_templateObject16$1 = _taggedTemplateLiteral(["\n    font-size: 16px;\n  "]))), gte('md')(_templateObject17$1 || (_templateObject17$1 = _taggedTemplateLiteral(["\n    width: 60%;\n  "]))), Grid);
var Seperator$1 = styled.div(_templateObject18$1 || (_templateObject18$1 = _taggedTemplateLiteral(["\n  width: 100%;\n  background-color: ", ";\n  height: 1px;\n  flex-basis: 100%;\n"])), color('shade.50'));
var RightAlignUnit = styled(Grid.Unit)(_templateObject19$1 || (_templateObject19$1 = _taggedTemplateLiteral(["\n  text-align: right;\n"])));
var StyledLendiTooltip = styled(LendiTooltip)(_templateObject20$1 || (_templateObject20$1 = _taggedTemplateLiteral(["\n  p {\n    margin: 0;\n  }\n"])));

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21, _templateObject22;
styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n\n  ", "\n"])), mx('auto'), between('xs', 'sm')(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    text-align: center;\n  "]))));
var StyledHeading = styled(Heading)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  ", "\n\n  strong {\n    margin-right: 4px;\n  }\n\n  small {\n    font-size: 14px;\n    display: inline-block;\n\n    ", ";\n  }\n"])), mt('md'), gte('sm')(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      font-size: 18px;\n    "]))));
var Wrapper = styled.div(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  background-color: ", ";\n  width: 100%;\n  box-sizing: border-box;\n  color: white;\n  z-index: 1;\n  border-top-left-radius: 5px;\n  border-top-right-radius: 5px;\n  border: none;\n  ", "\n  ", "\n\n  ", " {\n    height: 100%;\n  }\n\n  a {\n    align-self: flex-end;\n  }\n\n  ", " {\n    ", "\n  }\n"])), function () {
  return getColour('fill', 'contrast');
}, p({
  lg: 'lg',
  xs: 'md'
}), mt('md'), Grid, Body, mt('xxxs'));
var StyledGrid = styled(Grid)(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  position: relative;\n"])));
var SeperatorContainer = styled(Grid.Unit)(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref) {
  var interestOnly = _ref.interestOnly;
  return gte(interestOnly ? 'lg' : 'md')(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    padding: 0;\n  "])));
});
var Seperator = styled.div(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n  background-color: white;\n  box-sizing: border-box;\n  height: 1px;\n  margin: 28px auto;\n  padding: 0;\n  width: 100%;\n\n  ", "\n\n  ", "\n"])), function (_ref2) {
  var interestOnly = _ref2.interestOnly;
  return gte(interestOnly ? 'lg' : 'md')(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    width: unset;\n    margin: 0;\n    height: unset;\n  "])));
}, function (_ref3) {
  var interestOnly = _ref3.interestOnly;
  return gte(interestOnly ? 'lg' : 'md')(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    margin-left: -1px;\n    width: 1px;\n    min-height: 100%;\n  "])));
});
var FlexUnit = styled(Grid.Unit)(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-wrap: wrap;\n\n  ", " {\n    flex-basis: 100%;\n  }\n"])), Heading);
var Segment = styled.div(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n  width: 100%;\n\n  ", "\n\n  ", "\n\n  ", "\n\n  &:last-child {\n    ", "\n    display: flex;\n  }\n"])), gte('sm')(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    width: 50%;\n  "]))), function (_ref4) {
  var interestOnly = _ref4.interestOnly;
  return !interestOnly && gte('md')(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n      width: unset;\n    "])));
}, gte('lg')(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    width: 100%;\n  "]))), mt({
  xs: 'sm',
  sm: 'nil'
}));
var ResultDisplay = styled.div(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n  display: flex;\n\n  & > div {\n    max-width: 45%;\n    width: 50%;\n\n    ", "\n\n    ", "\n\n    &:last-child {\n      ", "\n    }\n  }\n\n  justify-content: space-between;\n"])), gte('sm')(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n      max-width: 50%;\n    "]))), gte('lg')(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n      width: unset;\n    "]))), mx({
  xs: 'nil',
  lg: 'auto'
}));
var NextSteps = styled(FlexUnit)(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n  ", "\n\n  ", "\n"])), between('xs', 'lg')(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n    justify-content: space-between;\n  "]))), gte('lg')(_templateObject22 || (_templateObject22 = _taggedTemplateLiteral(["\n    row-gap: 20px;\n    justify-content: center;\n  "]))));

var ResultsPanel = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var buttonLink = _ref.buttonLink,
    buttonText = _ref.buttonText,
    interestOnlyPeriod = _ref.interestOnlyPeriod,
    interestRate = _ref.interestRate,
    loanTerm = _ref.loanTerm,
    repayments = _ref.repayments,
    resultBody = _ref.resultBody,
    resultHeading = _ref.resultHeading,
    repaymentFrequency = _ref.repaymentFrequency;
  return /*#__PURE__*/jsx(Wrapper, {
    ref: forwardedRef,
    children: /*#__PURE__*/jsxs(StyledGrid, {
      gutter: "xxxl",
      children: [repayments.interestOnly ? /*#__PURE__*/jsxs(Grid.Unit, {
        size: {
          lg: 2 / 3,
          md: 1
        },
        children: [/*#__PURE__*/jsx(Heading, {
          size: "sm",
          children: "Estimated repayment"
        }), /*#__PURE__*/jsxs(ResultDisplay, {
          children: [/*#__PURE__*/jsxs("div", {
            children: [/*#__PURE__*/jsxs(StyledHeading, {
              size: {
                lg: 'xl',
                sm: 'lg',
                xs: 'md'
              },
              children: [/*#__PURE__*/jsxs("strong", {
                children: ["$", repayments.interestOnly.toLocaleString()]
              }), /*#__PURE__*/jsxs("small", {
                children: ["/ ", repaymentFrequency]
              })]
            }), /*#__PURE__*/jsxs(Body, {
              size: "sm",
              children: ["First ", interestOnlyPeriod, " years"]
            })]
          }), /*#__PURE__*/jsxs("div", {
            children: [/*#__PURE__*/jsxs(StyledHeading, {
              size: {
                lg: 'xl',
                sm: 'lg',
                xs: 'md'
              },
              children: [/*#__PURE__*/jsxs("strong", {
                children: ["$", repayments.principalAndInterest.toLocaleString()]
              }), /*#__PURE__*/jsxs("small", {
                children: ["/ ", repaymentFrequency]
              })]
            }), /*#__PURE__*/jsx(Body, {
              size: "sm",
              children: "Remainder of loan term"
            })]
          })]
        })]
      }) : /*#__PURE__*/jsx(Grid.Unit, {
        size: {
          sm: 1,
          md: 1 / 2
        },
        children: /*#__PURE__*/jsx(Grid, {
          children: /*#__PURE__*/jsxs(FlexUnit, {
            size: 1,
            children: [/*#__PURE__*/jsx(Heading, {
              size: "sm",
              children: "Estimated repayment"
            }), /*#__PURE__*/jsxs(StyledHeading, {
              size: {
                sm: 'xl',
                xs: 'lg'
              },
              children: [/*#__PURE__*/jsxs("strong", {
                children: ["$", repayments.principalAndInterest.toLocaleString()]
              }), /*#__PURE__*/jsxs("small", {
                children: ["/ ", repaymentFrequency]
              })]
            }), /*#__PURE__*/jsxs(Body, {
              size: "sm",
              children: ["Based on an interest rate of ", numeral(interestRate).format('0.00'), "% over ", loanTerm, " years", ' ']
            })]
          })
        })
      }), /*#__PURE__*/jsx(SeperatorContainer, {
        interestOnly: !!interestOnlyPeriod,
        size: interestOnlyPeriod ? {
          lg: 0,
          md: 1
        } : {
          md: 0,
          sm: 1
        },
        children: /*#__PURE__*/jsx(Seperator, {
          interestOnly: !!interestOnlyPeriod
        })
      }), /*#__PURE__*/jsx(Grid.Unit, {
        size: repayments.interestOnly ? {
          lg: 1 / 3,
          md: 1
        } : {
          md: 1 / 2,
          sm: 1
        },
        children: /*#__PURE__*/jsx(Grid, {
          children: /*#__PURE__*/jsxs(NextSteps, {
            size: repayments.interestOnly ? 1 : {
              md: 3 / 4,
              sm: 1
            },
            children: [/*#__PURE__*/jsxs(Segment, {
              interestOnly: !!interestOnlyPeriod,
              children: [/*#__PURE__*/jsx(Heading, {
                size: "sm",
                children: resultHeading
              }), /*#__PURE__*/jsx(Body, {
                size: "sm",
                children: resultBody
              })]
            }), /*#__PURE__*/jsx(Segment, {
              interestOnly: !!interestOnlyPeriod,
              children: buttonText && buttonLink && /*#__PURE__*/jsx(AnchorButton, {
                width: "100%",
                variant: "primary",
                isInverse: true,
                size: "md",
                href: embedMarketingTrackingIntoURL(buttonLink),
                children: buttonText
              })
            })]
          })
        })
      })]
    })
  });
});

var interestOnlyPeriodPrincipal = function interestOnlyPeriodPrincipal(interestOnlyPeriod, loanAmount) {
  var result = [];
  if (loanAmount <= 0) {
    return result;
  }
  result.push(loanAmount);
  for (var i = 0; i < interestOnlyPeriod; i++) {
    if (loanAmount > 0) {
      result.push(loanAmount);
    }
  }
  return result;
};
var principalRemaining = function principalRemaining(_ref) {
  var loanAmount = _ref.loanAmount,
    loanTerm = _ref.loanTerm,
    interestRate = _ref.interestRate,
    _ref$interestOnlyPeri = _ref.interestOnlyPeriod,
    interestOnlyPeriod = _ref$interestOnlyPeri === void 0 ? 0 : _ref$interestOnlyPeri;
  if (loanTerm <= 0) {
    return [];
  }
  var result = interestOnlyPeriodPrincipal(interestOnlyPeriod, loanAmount);
  var a = result[result.length - 1] || loanAmount;
  var n = (loanTerm - interestOnlyPeriod) * 12;
  var r = interestRate / 1200;
  var p = a * (r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1));
  for (var i = 1; i < n; i++) {
    var futureValue = a * Math.pow(1 + r, i) - p * ((Math.pow(1 + r, i) - 1) / r);
    if (i % 12 === 0 && futureValue > 0) {
      result.push(Math.round(futureValue));
    }
  }
  if (loanTerm > interestOnlyPeriod && result[result.length - 1] !== 0 || loanTerm && loanTerm < interestOnlyPeriod && result[result.length - 1] !== 0) {
    result.push(0);
  }
  return result;
};

var items = [{
  label: 'Weekly',
  value: 52
}, {
  label: 'Fortnightly',
  value: 26
}, {
  label: 'Monthly',
  value: 12
}];
var DEFAULT_VALUES = {
  loanTerm: 30,
  loanAmount: '450,000',
  interestOnlyPeriod: 4,
  interestRate: '4.34'
};
var MAXIMUM_VALUES = {
  loanTerm: 30,
  loanAmount: 20000000,
  interestOnlyPeriod: 9,
  interestRate: 20
};
var schema = yup.object().shape({
  loanAmount: yup.mixed().transform(function (string) {
    return numeral(string).value();
  }).test({
    name: 'min',
    message: 'Loan amount must be greater than $10,000',
    test: function test(loanAmount) {
      return loanAmount >= 10000;
    }
  }).test({
    name: 'max',
    message: 'Loan amount must be equal to or less than $20 million',
    test: function test(loanAmount) {
      return loanAmount <= MAXIMUM_VALUES.loanAmount;
    }
  }),
  loanTerm: yup.number().typeError('Loan term must be greater than 0 years').min(1, 'Loan term must be greater than 0 years').max(MAXIMUM_VALUES.loanTerm, 'Loan term must be equal to or less than 30 years').integer('Please enter a whole number').required('Loan term must be greater than 0 years'),
  interestRate: yup.number().typeError('Interest rate must be greater than 0%').min(0.01, 'Interest rate must be greater than 0%').max(MAXIMUM_VALUES.interestRate, 'Interest rate must be equal to or less than 20%').required('Interest rate must be greater than 0%'),
  interestOnlyPeriod: yup.number().typeError('Period must be greater than 0 years').min(1, 'Period must be greater than 0 years').max(MAXIMUM_VALUES.interestOnlyPeriod, 'Period must be equal to or less than 9 years').integer('Please enter a whole number')
});
var RepaymentsCalculator = function RepaymentsCalculator(_ref) {
  var buttonText = _ref.buttonText,
    buttonLink = _ref.buttonLink,
    calculatorHeading = _ref.calculatorHeading,
    disclaimer = _ref.disclaimer,
    graphHeading = _ref.graphHeading,
    radioTooltipContent = _ref.radioTooltipContent,
    resultBody = _ref.resultBody,
    resultHeading = _ref.resultHeading,
    showGraph = _ref.showGraph;
  var _useState = useState(items[2]),
    _useState2 = _slicedToArray(_useState, 2),
    repaymentFrequency = _useState2[0],
    setRepaymentFrequency = _useState2[1];
  var _useState3 = useState(RepaymentType.PrincipalAndInterest),
    _useState4 = _slicedToArray(_useState3, 2),
    repaymentType = _useState4[0],
    setRepaymentType = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    chartData = _useState6[0],
    setChartData = _useState6[1];
  var resultElement = useRef(null);
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    results = _useState8[0],
    setResults = _useState8[1];
  var totalRepayments = useMemo(function () {
    if (!results) return 0;
    var interestOnlyPeriod = results.interestOnlyPeriod || 0;
    var principalAndInterestRepayments = results.repayments.principalAndInterest * repaymentFrequency.value * (results.loanTerm - interestOnlyPeriod);
    var interestOnlyRepayments = results.repayments.interestOnly && interestOnlyPeriod ? interestOnlyPeriod * repaymentFrequency.value * results.repayments.interestOnly : 0;
    return principalAndInterestRepayments + interestOnlyRepayments;
  }, [results]);
  var generateId = useGenerateId();
  var _useForm = useForm({
      resolver: yupResolver(schema),
      reValidateMode: 'onChange',
      mode: 'onChange',
      defaultValues: DEFAULT_VALUES
    }),
    errors = _useForm.errors,
    handleSubmit = _useForm.handleSubmit,
    register = _useForm.register,
    reset = _useForm.reset,
    trigger = _useForm.trigger,
    _useForm$formState = _useForm.formState,
    isValid = _useForm$formState.isValid,
    isDirty = _useForm$formState.isDirty,
    isSubmitted = _useForm$formState.isSubmitted;
  var loanAmountHandler = createInputHandler(MAXIMUM_VALUES.loanAmount, function () {
    return trigger('loanAmount');
  });
  var loanTermHandler = createInputHandler(MAXIMUM_VALUES.loanTerm, function () {
    return trigger('loanTerm');
  });
  var interestRateHandler = createInputHandler(MAXIMUM_VALUES.interestRate, function () {
    return trigger('interestRate');
  }, '0.00');
  var interestOnlyPeriodHandler = createInputHandler(MAXIMUM_VALUES.interestOnlyPeriod, function () {
    return trigger('interestOnlyPeriod');
  });
  var disableCalculateButton = useMemo(function () {
    var hasChanged = (results === null || results === void 0 ? void 0 : results.repaymentFrequency) !== repaymentFrequency.label || isDirty || !isSubmitted;
    return isValid && hasChanged;
  }, [isSubmitted, isValid, isDirty, repaymentFrequency, results]);
  var onSubmit = function onSubmit(_ref2) {
    var _ref2$interestOnlyPer = _ref2.interestOnlyPeriod,
      interestOnlyPeriod = _ref2$interestOnlyPer === void 0 ? 0 : _ref2$interestOnlyPer,
      interestRate = _ref2.interestRate,
      loanAmount = _ref2.loanAmount,
      loanTerm = _ref2.loanTerm;
    var repayments = {
      principalAndInterest: Math.round(calculateRepayment({
        loanTerm: loanTerm - interestOnlyPeriod,
        loanAmount: loanAmount,
        interestOnlyPeriod: 0,
        interestRate: interestRate,
        repaymentFrequency: repaymentFrequency.value
      })),
      interestOnly: !!interestOnlyPeriod ? Math.round(calculateRepayment({
        loanTerm: loanTerm,
        loanAmount: loanAmount,
        interestOnlyPeriod: interestOnlyPeriod,
        interestRate: interestRate,
        repaymentFrequency: repaymentFrequency.value
      })) : undefined
    };

    // Reset form state for disabling calculate button
    reset(_objectSpread(_objectSpread({}, DEFAULT_VALUES), {}, {
      interestOnlyPeriod: interestOnlyPeriod,
      interestRate: numeral(interestRate).format('0.00'),
      loanAmount: loanAmount.toLocaleString(),
      loanTerm: loanTerm
    }));
    setResults({
      loanTerm: loanTerm,
      loanAmount: loanAmount,
      interestOnlyPeriod: interestOnlyPeriod,
      interestRate: interestRate,
      repayments: repayments,
      repaymentFrequency: repaymentFrequency.label
    });
    setChartData(principalRemaining({
      loanTerm: loanTerm,
      loanAmount: loanAmount,
      interestOnlyPeriod: interestOnlyPeriod,
      interestRate: interestRate
    }).map(function (remainingAmount, index) {
      return {
        years: index,
        remainingAmount: remainingAmount
      };
    }));
  };
  return /*#__PURE__*/jsxs(Wrapper$1, {
    children: [/*#__PURE__*/jsxs(Calculator, {
      onSubmit: handleSubmit(onSubmit),
      children: [calculatorHeading && /*#__PURE__*/jsx(Title, {
        size: 'sm',
        children: calculatorHeading
      }), /*#__PURE__*/jsxs(Grid, {
        children: [/*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 3,
            sm: 1 / 2
          },
          children: [/*#__PURE__*/jsx("label", {
            htmlFor: generateId('loanAmountValue'),
            children: /*#__PURE__*/jsx(StyledHeading$1, {
              size: "xxs",
              children: "Loan amount"
            })
          }), /*#__PURE__*/jsx(Input, {
            ref: register,
            id: generateId('loanAmountValue'),
            name: "loanAmount",
            isFullWidth: true,
            size: "md",
            type: "text",
            onChange: function onChange() {
              return undefined;
            },
            onBlur: loanAmountHandler.blur,
            onFocus: loanAmountHandler.focus,
            before: /*#__PURE__*/jsx(InputBefore, {
              children: "$"
            }),
            isError: Boolean(errors.loanAmount)
          }), errors.loanAmount && /*#__PURE__*/jsxs(Text, {
            appearance: "error",
            tag: "p",
            variant: "detail2",
            children: [/*#__PURE__*/jsx(Error, {
              color: getColour('interaction', 'error')
            }), " ", errors.loanAmount.message]
          })]
        }), /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 3,
            xs: 1 / 2
          },
          children: [/*#__PURE__*/jsx("label", {
            htmlFor: generateId('interestRateValue'),
            children: /*#__PURE__*/jsx(StyledHeading$1, {
              size: "xxs",
              children: "Interest rate"
            })
          }), /*#__PURE__*/jsx(Input, {
            ref: register,
            id: generateId('interestRateValue'),
            isFullWidth: true,
            name: "interestRate",
            size: "md",
            type: "text",
            onChange: function onChange() {
              return undefined;
            },
            onBlur: interestRateHandler.blur,
            onFocus: interestRateHandler.focus,
            after: /*#__PURE__*/jsx(InputAfter, {
              size: "md",
              children: "%"
            }),
            isError: Boolean(errors.interestRate)
          }), errors.interestRate && /*#__PURE__*/jsxs(Text, {
            appearance: "error",
            tag: "p",
            variant: "detail2",
            children: [/*#__PURE__*/jsx(Error, {
              color: getColour('interaction', 'error')
            }), " ", errors.interestRate.message]
          })]
        }), /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 3,
            xs: 1 / 2
          },
          children: [/*#__PURE__*/jsx("label", {
            htmlFor: generateId('loanTermValue'),
            children: /*#__PURE__*/jsx(StyledHeading$1, {
              size: "xxs",
              children: "Loan term"
            })
          }), /*#__PURE__*/jsx(Input, {
            name: "loanTerm",
            ref: register,
            id: generateId('loanTermValue'),
            isFullWidth: true,
            size: "md",
            onChange: function onChange() {
              return undefined;
            },
            onBlur: loanTermHandler.blur,
            onFocus: loanTermHandler.focus,
            type: "text",
            after: /*#__PURE__*/jsx(InputAfter, {
              children: "years"
            }),
            isError: Boolean(errors.loanTerm)
          }), errors.loanTerm && /*#__PURE__*/jsxs(Text, {
            appearance: "error",
            tag: "p",
            variant: "detail2",
            children: [/*#__PURE__*/jsx(Error, {
              color: getColour('interaction', 'error')
            }), " ", errors.loanTerm.message]
          })]
        }), /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 3,
            sm: 1 / 2
          },
          children: [/*#__PURE__*/jsx(StyledHeading$1, {
            size: "xxs",
            children: "Repayment frequency"
          }), /*#__PURE__*/jsx(StyledSelect, {
            isFullWidth: true,
            options: items,
            value: repaymentFrequency,
            onChangeItem: function onChangeItem(item) {
              return setRepaymentFrequency(item);
            }
          })]
        }), /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: repaymentType === RepaymentType.InterestOnly ? 1 / 3 : 1 / 2,
            sm: repaymentType === RepaymentType.InterestOnly ? 1 / 2 : 2 / 3
          },
          children: [/*#__PURE__*/jsxs(StyledHeading$1, {
            size: "xxs",
            children: ["Repayment type", ' ', /*#__PURE__*/jsx(StyledLendiTooltip, {
              content: radioTooltipContent,
              position: "right",
              children: /*#__PURE__*/jsx(Help, {
                height: "20px",
                width: "20px",
                color: getColour('interaction', 'main')
              })
            })]
          }), /*#__PURE__*/jsxs(StyledRadioGroup, {
            onChange: function onChange(e) {
              return setRepaymentType(e.target.value);
            },
            selectedValue: repaymentType,
            direction: "row",
            children: [/*#__PURE__*/jsx(RadioGroup.Radio, {
              id: "principal-and-interest-radio",
              label: "Principal and interest",
              value: RepaymentType.PrincipalAndInterest
            }), /*#__PURE__*/jsx(RadioGroup.Radio, {
              id: "interest-only-radio",
              label: "Interest only",
              value: RepaymentType.InterestOnly
            })]
          })]
        }), repaymentType === RepaymentType.InterestOnly ? /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 3,
            sm: 1 / 2
          },
          children: [/*#__PURE__*/jsx("label", {
            htmlFor: generateId('interestOnlyPeriodValue'),
            children: /*#__PURE__*/jsx(StyledHeading$1, {
              size: "xxs",
              children: "Interest only period"
            })
          }), /*#__PURE__*/jsx(Input, {
            name: "interestOnlyPeriod",
            id: generateId('interestOnlyPeriodValue'),
            ref: register,
            isFullWidth: true,
            size: "md",
            onChange: function onChange() {
              return undefined;
            },
            onBlur: interestOnlyPeriodHandler.blur,
            onFocus: interestOnlyPeriodHandler.focus,
            type: "text",
            after: /*#__PURE__*/jsx(InputAfter, {
              children: "years"
            }),
            isError: Boolean(errors.interestOnlyPeriod)
          }), errors.interestOnlyPeriod && /*#__PURE__*/jsxs(Text, {
            appearance: "error",
            tag: "p",
            variant: "detail2",
            children: [/*#__PURE__*/jsx(Error, {
              color: getColour('interaction', 'error')
            }), " ", errors.interestOnlyPeriod.message]
          })]
        }) : null, /*#__PURE__*/jsx(StyledUnit, {
          size: 1,
          children: /*#__PURE__*/jsx(StyledButtonWrapper, {
            children: /*#__PURE__*/jsx(Button, {
              "data-testid": "calculate-button",
              type: "submit",
              variant: "primary",
              size: "md",
              width: "100%"
              // Form needs to be valid AND values need to be changed from last submit
              ,
              isDisabled: !disableCalculateButton,
              children: "Calculate"
            })
          })
        })]
      })]
    }), results && /*#__PURE__*/jsx(ResultsPanel, _objectSpread(_objectSpread({}, results), {}, {
      buttonLink: buttonLink,
      buttonText: buttonText,
      ref: resultElement,
      repaymentFrequency: results.repaymentFrequency.substring(0, results.repaymentFrequency.length - 2).toLowerCase(),
      resultBody: resultBody,
      resultHeading: resultHeading
    })), results && /*#__PURE__*/jsxs(ResultsWrapper, {
      children: [showGraph && graphHeading && /*#__PURE__*/jsx(GraphHeading, {
        size: "sm",
        align: "center",
        children: graphHeading
      }), chartData && showGraph && /*#__PURE__*/jsx(Chart, {
        chartData: chartData,
        xAxis: {
          key: 'years',
          value: results.loanTerm,
          label: 'Years'
        },
        yAxis: {
          key: 'remainingAmount',
          value: results.loanAmount,
          label: 'Amount owing (‘000s)'
        },
        lines: [{
          color: getColour('interaction', 'main') || '#00c0a5',
          dataKey: 'remainingAmount'
        }],
        tooltip: /*#__PURE__*/jsx(Tooltip, {
          cursor: false,
          content: /*#__PURE__*/jsx(GraphTooltip, {
            totalYears: results.loanTerm
          })
        })
      }), showGraph && /*#__PURE__*/jsx(PaymentSummary, {
        children: /*#__PURE__*/jsxs(Grid, {
          children: [/*#__PURE__*/jsx(Grid.Unit, {
            size: 3 / 4,
            children: "Total interest charged"
          }), /*#__PURE__*/jsxs(RightAlignUnit, {
            size: 1 / 4,
            children: ["$", (totalRepayments - results.loanAmount).toLocaleString()]
          }), /*#__PURE__*/jsx(Seperator$1, {}), /*#__PURE__*/jsx(Grid.Unit, {
            size: 3 / 4,
            children: "Total repayments"
          }), /*#__PURE__*/jsxs(RightAlignUnit, {
            size: 1 / 4,
            children: ["$", totalRepayments.toLocaleString()]
          })]
        })
      }), disclaimer && /*#__PURE__*/jsx(Summary, {
        children: disclaimer
      })]
    })]
  });
};

export { RepaymentsCalculator as default };
